import { useState, useEffect } from 'react';
import { Check, AlertTriangle, Droplets, Wind, HelpCircle } from 'lucide-react';
import LabBench from '../../LabBench';
import InfoBox from '../../InfoBox';
import { Crucible, Dropper } from '@/components/lab';

const stepInfoContent: Record<number, { title: string; content: string }> = {
  1: {
    title: 'Fume Hood Safety',
    content: 'Concentrated nitric acid produces toxic brown nitrogen dioxide (NO₂) fumes when reacting with metals. The fume hood exhausts these gases away from you. Always lower the sash to minimize exposure and improve draft efficiency.'
  },
  2: {
    title: 'Adding Acid Dropwise',
    content: 'Adding acid slowly prevents violent reactions and splattering. The reaction Sn + 4HNO₃ → SnO₂ + 4NO₂ + 2H₂O produces heat. Dropwise addition controls the reaction rate and ensures complete dissolution of the tin.'
  },
  3: {
    title: 'Reaction Completion',
    content: 'Continue adding acid until brown NO₂ fumes stop evolving. This indicates the tin has completely reacted. The white/cream residue forming is hydrated tin oxide, which will be converted to SnO₂ by heating.'
  }
};

export default function Part4AcidAddition() {
  const [sashLowered, setSashLowered] = useState(false);
  const [acidDrops, setAcidDrops] = useState(0);
  const [tinDissolvedPercent, setTinDissolvedPercent] = useState(0);
  const [fumesIntensity, setFumesIntensity] = useState(0);
  const [reactionComplete, setReactionComplete] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [isDropping, setIsDropping] = useState(false);
  const [showInfo, setShowInfo] = useState<number | null>(null);

  // Reaction progress based on acid drops
  useEffect(() => {
    if (acidDrops > 0) {
      const dissolved = Math.min((acidDrops / 20) * 100, 100);
      setTinDissolvedPercent(dissolved);
      setFumesIntensity(dissolved < 100 ? Math.min(acidDrops * 5, 80) : Math.max(0, 80 - (acidDrops - 20) * 10));

      if (dissolved >= 100 && fumesIntensity < 10) {
        setReactionComplete(true);
      }
    }
  }, [acidDrops]);

  const handleLowerSash = () => {
    if (currentStep === 1) {
      setSashLowered(true);
      setCurrentStep(2);
    }
  };

  const handleAddAcid = () => {
    if (sashLowered && !reactionComplete && currentStep >= 2) {
      setIsDropping(true);
      setTimeout(() => {
        setAcidDrops(prev => prev + 1);
        setIsDropping(false);
        if (currentStep === 2) setCurrentStep(3);
      }, 500);
    }
  };

  const completedCount = [sashLowered, acidDrops > 0, reactionComplete].filter(Boolean).length;

  // Generate fume particles
  const fumeParticles = Array.from({ length: Math.floor(fumesIntensity / 10) }, (_, i) => ({
    id: i,
    x: 45 + Math.random() * 30,
    delay: Math.random() * 2
  }));

  return (
    <div className="experiment-layout">
      <div className="steps-panel">
        <div className="steps-header">
          <AlertTriangle size={16} />
          <h3>Part IV: Add Nitric Acid</h3>
        </div>

        <div className="warning-banner">
          <AlertTriangle size={14} />
          <span>FUME HOOD REQUIRED</span>
        </div>

        <div className="steps-list">
          {[
            { id: 1, title: 'Lower Fume Hood Sash', hint: 'Safety first!', done: sashLowered },
            { id: 2, title: 'Add Acid Dropwise', hint: 'Slowly, one drop at a time', done: acidDrops > 0 },
            { id: 3, title: 'Continue Until Complete', hint: 'Until fumes stop', done: reactionComplete }
          ].map((step) => (
            <div
              key={step.id}
              className={`step-item ${step.done ? 'completed' : ''} ${currentStep === step.id ? 'active' : ''}`}
            >
              <div className={`step-number ${step.done ? 'done' : ''}`}>
                {step.done ? <Check size={12} /> : step.id}
              </div>
              <div className="step-content">
                <span className="step-title">{step.title}</span>
                {currentStep === step.id && !step.done && (
                  <span className="step-hint">{step.hint}</span>
                )}
              </div>
              {stepInfoContent[step.id] && (
                <button
                  className="step-info-btn"
                  onClick={(e) => { e.stopPropagation(); setShowInfo(step.id); }}
                >
                  ?
                </button>
              )}
            </div>
          ))}
        </div>

        <div className="reaction-stats">
          <div className="stat-row">
            <span>Acid drops:</span>
            <span className="value">{acidDrops}</span>
          </div>
          <div className="stat-row">
            <span>Tin dissolved:</span>
            <span className="value">{tinDissolvedPercent.toFixed(0)}%</span>
          </div>
          <div className="stat-row">
            <span>NO₂ fumes:</span>
            <div className="fume-bar">
              <div className="fume-level" style={{ width: `${fumesIntensity}%` }} />
            </div>
          </div>
        </div>
      </div>

      <LabBench
        experimentName="Add Nitric Acid"
        completedCount={completedCount}
        totalSteps={3}
        equipment={['Fume hood', 'Concentrated HNO₃', 'Dropper', 'Crucible']}
        labMode="guided"
        currentStep={currentStep}
      >
        <div className="acid-workspace">
          {/* Fume Hood Visualization */}
          <div className="fume-hood">
            <div className="hood-top">
              <Wind size={16} />
              <span>FUME HOOD</span>
              {sashLowered && <span className="status-light active" />}
            </div>

            <div className="hood-body">
              {/* Sash */}
              <div
                className={`sash ${sashLowered ? 'lowered' : ''} ${currentStep === 1 ? 'clickable' : ''}`}
                onClick={handleLowerSash}
              >
                <div className="sash-handle">
                  {!sashLowered && <span>Click to lower</span>}
                </div>
              </div>

              {/* Work area */}
              <div className="work-area">
                {/* Crucible with reaction */}
                <div className="crucible-container">
                  <svg viewBox="0 0 100 120" width={80} height={100}>
                    {/* Crucible */}
                    <path
                      d="M25 50 L25 35 Q25 20 50 20 Q75 20 75 35 L75 50 Q75 60 50 60 Q25 60 25 50"
                      fill="#D3D3D3"
                      stroke="#888"
                      strokeWidth="2"
                    />

                    {/* Tin/reaction contents */}
                    <ellipse
                      cx="50"
                      cy="45"
                      rx="20"
                      ry="8"
                      fill={tinDissolvedPercent < 50 ? '#C0C0C0' : tinDissolvedPercent < 100 ? '#A0A0A0' : '#FFFEF5'}
                    />

                    {/* Bubbles during reaction */}
                    {acidDrops > 0 && !reactionComplete && (
                      <>
                        <circle cx="40" cy="40" r="2" fill="rgba(255,255,255,0.6)" className="bubble" />
                        <circle cx="55" cy="42" r="1.5" fill="rgba(255,255,255,0.6)" className="bubble delay-1" />
                        <circle cx="48" cy="38" r="2" fill="rgba(255,255,255,0.6)" className="bubble delay-2" />
                      </>
                    )}

                    {/* Brown NO2 fumes */}
                    {fumeParticles.map(p => (
                      <circle
                        key={p.id}
                        cx={p.x}
                        cy={30}
                        r={3}
                        fill="#8B4513"
                        opacity={fumesIntensity / 100}
                        className="fume"
                        style={{ animationDelay: `${p.delay}s` }}
                      />
                    ))}
                  </svg>
                </div>

                {/* Acid Dropper */}
                {sashLowered && (
                  <div
                    className={`dropper-container ${!reactionComplete ? 'clickable' : ''}`}
                    onClick={handleAddAcid}
                  >
                    <svg viewBox="0 0 40 100" width={40} height={100}>
                      {/* Dropper bulb */}
                      <ellipse cx="20" cy="15" rx="12" ry="12" fill="#333" />
                      {/* Dropper body */}
                      <rect x="16" y="25" width="8" height="50" fill="rgba(200,220,240,0.5)" stroke="#7A9BB8" />
                      {/* Acid inside */}
                      <rect x="17" y="30" width="6" height="40" fill="#FFFFE0" opacity="0.7" />
                      {/* Tip */}
                      <path d="M18 75 L22 75 L20 90 Z" fill="rgba(200,220,240,0.5)" stroke="#7A9BB8" />

                      {/* Dropping animation */}
                      {isDropping && (
                        <circle cx="20" cy="95" r="3" fill="#FFFFE0" className="drop" />
                      )}
                    </svg>
                    <span className="equipment-label">Conc. HNO₃</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {reactionComplete && (
            <div className="status-message success">
              <Check size={20} />
              Reaction complete! Tin has fully reacted with nitric acid.
            </div>
          )}

          <div className="reaction-equation">
            <h4>Reaction:</h4>
            <p>Sn + 4HNO₃ → SnO₂ + 4NO₂↑ + 2H₂O</p>
            <p className="note">Brown NO₂ gas is toxic - work in fume hood only!</p>
          </div>
        </div>

        <style>{`
          .acid-workspace {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 20px;
            padding: 20px;
            width: 100%;
          }

          .warning-banner {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 8px 12px;
            background: rgba(239, 68, 68, 0.2);
            border-radius: 6px;
            color: #EF4444;
            font-size: 11px;
            font-weight: 700;
            margin: -8px 0 8px 0;
          }

          .fume-hood {
            background: var(--bg-elevated);
            border: 2px solid var(--border);
            border-radius: 12px;
            overflow: hidden;
            width: 350px;
          }

          .hood-top {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 10px 16px;
            background: #444;
            color: white;
            font-size: 12px;
            font-weight: 600;
          }

          .status-light {
            width: 8px;
            height: 8px;
            border-radius: 50%;
            background: #666;
            margin-left: auto;
          }

          .status-light.active {
            background: #4ADE80;
            box-shadow: 0 0 8px #4ADE80;
          }

          .hood-body {
            position: relative;
            height: 250px;
            background: linear-gradient(180deg, #2A2A2A 0%, #1A1A1A 100%);
          }

          .sash {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            height: 80%;
            background: rgba(200, 230, 255, 0.2);
            border-bottom: 4px solid #7A9BB8;
            transition: height 0.5s ease;
            display: flex;
            align-items: flex-end;
            justify-content: center;
          }

          .sash.lowered {
            height: 30%;
          }

          .sash.clickable {
            cursor: pointer;
          }

          .sash.clickable:hover {
            background: rgba(200, 230, 255, 0.3);
          }

          .sash-handle {
            padding: 4px 12px;
            background: #666;
            border-radius: 4px 4px 0 0;
            font-size: 10px;
            color: white;
          }

          .work-area {
            position: absolute;
            bottom: 20px;
            left: 0;
            right: 0;
            display: flex;
            justify-content: center;
            gap: 40px;
            align-items: flex-end;
          }

          .crucible-container,
          .dropper-container {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 4px;
          }

          .clickable {
            cursor: pointer;
          }

          .equipment-label {
            font-size: 10px;
            color: var(--text-muted);
          }

          .bubble {
            animation: bubble 1s ease-out infinite;
          }

          .bubble.delay-1 { animation-delay: 0.3s; }
          .bubble.delay-2 { animation-delay: 0.6s; }

          @keyframes bubble {
            0% { transform: translateY(0); opacity: 0.8; }
            100% { transform: translateY(-15px); opacity: 0; }
          }

          .fume {
            animation: rise 2s ease-out infinite;
          }

          @keyframes rise {
            0% { transform: translateY(0); opacity: 0.6; }
            100% { transform: translateY(-50px); opacity: 0; }
          }

          .drop {
            animation: fall 0.3s ease-in forwards;
          }

          @keyframes fall {
            0% { transform: translateY(0); }
            100% { transform: translateY(20px); opacity: 0; }
          }

          .reaction-stats {
            background: var(--bg-elevated);
            border-radius: 8px;
            padding: 12px;
            margin-top: 12px;
          }

          .stat-row {
            display: flex;
            justify-content: space-between;
            align-items: center;
            font-size: 12px;
            padding: 4px 0;
          }

          .stat-row .value {
            font-family: monospace;
            font-weight: 600;
          }

          .fume-bar {
            width: 60px;
            height: 8px;
            background: var(--border);
            border-radius: 4px;
            overflow: hidden;
          }

          .fume-level {
            height: 100%;
            background: #8B4513;
            transition: width 0.3s;
          }

          .status-message {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 12px 20px;
            border-radius: 8px;
            font-weight: 500;
          }

          .status-message.success {
            background: rgba(0, 168, 107, 0.2);
            color: var(--secondary);
          }

          .reaction-equation {
            background: var(--bg-elevated);
            border-left: 3px solid var(--primary);
            border-radius: 8px;
            padding: 16px;
            max-width: 350px;
          }

          .reaction-equation h4 {
            margin: 0 0 8px 0;
            font-size: 12px;
            color: var(--text-secondary);
          }

          .reaction-equation p {
            margin: 0;
            font-family: monospace;
            font-size: 14px;
            color: var(--text-primary);
          }

          .reaction-equation .note {
            margin-top: 8px;
            font-family: inherit;
            font-size: 11px;
            color: #EF4444;
          }
          .step-info-btn {
            width: 18px;
            height: 18px;
            border-radius: 50%;
            background: var(--accent);
            color: var(--bg-dark);
            font-size: 11px;
            font-weight: 700;
            border: none;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            transition: transform 0.15s;
          }
          .step-info-btn:hover {
            transform: scale(1.1);
          }
        `}</style>
      </LabBench>

      {showInfo && stepInfoContent[showInfo] && (
        <InfoBox
          step={showInfo}
          experimentId="exp11"
          stepInfo={stepInfoContent[showInfo]}
          onClose={() => setShowInfo(null)}
        />
      )}
    </div>
  );
}
